import cn from "classnames";
import { useEffect, useRef, useState } from "react";
import { useTranslation } from "react-i18next";

import {
  AiAssistant,
  EAiAssistantChannel,
  ESandboxToolExecMode,
  SandboxToolTrace
} from "../../aiAgents.types";
import { useSandbox } from "../../apis";
import { AgentAvatar } from "../AgentAvatar";

import styles from "./Sandbox.module.scss";

const CHANNEL_COLOR: Record<EAiAssistantChannel, string> = {
  [EAiAssistantChannel.WhatsApp]: "#733FB7",
  [EAiAssistantChannel.InAppSupport]: "#03BF9D"
};

const SendIcon = () => (
  <svg width="20" height="20" viewBox="0 0 20 20" fill="none">
    <path
      d="M2 10L18 2L10 19L8 11.5L2 10Z"
      fill="#fff"
      stroke="#fff"
      strokeWidth="1.2"
      strokeLinejoin="round"
    />
  </svg>
);

const ResetIcon = () => (
  <svg width="16" height="16" viewBox="0 0 20 20" fill="none">
    <path
      d="M15.5 6.5A6.5 6.5 0 1 0 17 11"
      stroke="#7e7e8b"
      strokeWidth="1.6"
      strokeLinecap="round"
    />
    <path
      d="M17 3v4h-4"
      stroke="#7e7e8b"
      strokeWidth="1.6"
      strokeLinecap="round"
      strokeLinejoin="round"
    />
  </svg>
);

type ChatRole = "user" | "assistant" | "system";

interface ChatMessage {
  id: string;
  role: ChatRole;
  text: string;
  toolTrace?: SandboxToolTrace[];
}

interface Props {
  assistant: AiAssistant;
}

const Sandbox = ({ assistant }: Props) => {
  const { t } = useTranslation();
  const { createSession, sendMessage, resetSession } = useSandbox(assistant.id);

  const [messages, setMessages] = useState<ChatMessage[]>([]);
  const [input, setInput] = useState("");
  const sessionIdRef = useRef<string | null>(null);
  const idRef = useRef(0);
  const scrollRef = useRef<HTMLDivElement>(null);

  const accent = CHANNEL_COLOR[assistant.channel];
  const pending = createSession.isLoading || sendMessage.isLoading;

  const MODE_LABEL: Record<ESandboxToolExecMode, string> = {
    [ESandboxToolExecMode.Executed]: t("ran for real"),
    [ESandboxToolExecMode.Simulated]: t("simulated"),
    [ESandboxToolExecMode.Terminal]: t("handoff")
  };

  const nextId = () => `m${(idRef.current += 1)}`;
  const push = (msg: ChatMessage) => setMessages((prev) => [...prev, msg]);

  useEffect(() => {
    scrollRef.current?.scrollTo({
      top: scrollRef.current.scrollHeight,
      behavior: "smooth"
    });
  }, [messages, pending]);

  const handleSend = async () => {
    const text = input.trim();
    if (!text || pending) return;

    setInput("");
    push({ id: nextId(), role: "user", text });

    try {
      let sessionId = sessionIdRef.current;
      if (!sessionId) {
        const session = await createSession.mutateAsync();
        sessionId = session.sessionId;
        sessionIdRef.current = sessionId;
      }

      const result = await sendMessage.mutateAsync({ sessionId, text });
      const trace = result.toolTrace;

      if (result.reply.kind === "reply" && result.reply.text) {
        push({
          id: nextId(),
          role: "assistant",
          text: result.reply.text,
          toolTrace: trace
        });
        return;
      }

      // escalate / exhausted: still surface any tools the agent tried, then a note.
      if (trace.length > 0) {
        push({ id: nextId(), role: "assistant", text: "", toolTrace: trace });
      }
      push({
        id: nextId(),
        role: "system",
        text:
          result.reply.kind === "escalate"
            ? t("Assistant handed off to a human. Reason: {{reason}}", {
                reason: result.reply.reason || t("unspecified")
              })
            : t("The assistant produced no reply (reached the step limit).")
      });
    } catch {
      // Network/validation errors are surfaced via toast by the mutation.
    }
  };

  const handleReset = () => {
    const sessionId = sessionIdRef.current;
    setMessages([]);
    setInput("");
    sessionIdRef.current = null;
    if (sessionId) {
      resetSession.mutate(sessionId);
    }
  };

  const handleKeyDown = (event: React.KeyboardEvent<HTMLInputElement>) => {
    if (event.key === "Enter" && !event.shiftKey) {
      event.preventDefault();
      void handleSend();
    }
  };

  const renderTool = (tool: SandboxToolTrace, index: number) => (
    <details key={`${tool.toolName}-${index}`} className={styles.tool}>
      <summary className={styles.toolSummary}>
        <span className={styles.toolName}>{tool.toolName}</span>
        <span className={cn(styles.toolBadge, styles[`badge_${tool.mode}`])}>
          {MODE_LABEL[tool.mode]}
        </span>
        {tool.isError && <span className={styles.toolErr}>{t("error")}</span>}
      </summary>
      <div className={styles.toolBody}>
        {tool.note && <div className={styles.toolNote}>{tool.note}</div>}
        <div className={styles.toolLabel}>{t("Input")}</div>
        <pre className={styles.toolPre}>
          {JSON.stringify(tool.input, null, 2)}
        </pre>
        <div className={styles.toolLabel}>{t("Result")}</div>
        <pre className={styles.toolPre}>{tool.resultPreview}</pre>
      </div>
    </details>
  );

  const renderMessage = (message: ChatMessage) => {
    if (message.role === "system") {
      return (
        <div key={message.id} className={styles.systemNote}>
          {message.text}
        </div>
      );
    }

    const isUser = message.role === "user";

    return (
      <div
        key={message.id}
        className={cn(
          styles.row,
          isUser ? styles.rowUser : styles.rowAssistant
        )}
      >
        {message.text && (
          <div
            className={cn(
              styles.bubble,
              isUser ? styles.bubbleUser : styles.bubbleAssistant
            )}
            style={isUser ? { backgroundColor: accent } : undefined}
          >
            {message.text}
          </div>
        )}
        {message.toolTrace && message.toolTrace.length > 0 && (
          <div className={styles.tools}>
            {message.toolTrace.map(renderTool)}
          </div>
        )}
      </div>
    );
  };

  const isEmpty = messages.length === 0;

  return (
    <div className={styles.sandbox}>
      <div className={styles.header}>
        <span className={styles.headerTitle}>{t("Sandbox")}</span>
        {!isEmpty && (
          <button
            type="button"
            className={styles.resetBtn}
            onClick={handleReset}
            disabled={pending}
          >
            <ResetIcon />
            <span>{t("Reset")}</span>
          </button>
        )}
      </div>

      {isEmpty ? (
        <div className={styles.body}>
          <AgentAvatar
            letter={(assistant.name[0] ?? "A").toUpperCase()}
            color={accent}
            size={64}
          />
          <div className={styles.agentName}>{assistant.name}</div>
          <div className={styles.heading}>
            {t(
              "This is a sandbox environment. You can test your assistant behavior in real conditions here."
            )}
          </div>
          <div className={styles.note}>
            {t(
              "Catalog lookups run for real; orders, messages and other side effects are simulated."
            )}
          </div>
        </div>
      ) : (
        <div className={styles.messages} ref={scrollRef}>
          {messages.map(renderMessage)}
          {pending && (
            <div className={cn(styles.row, styles.rowAssistant)}>
              <div
                className={cn(
                  styles.bubble,
                  styles.bubbleAssistant,
                  styles.typing
                )}
              >
                <span />
                <span />
                <span />
              </div>
            </div>
          )}
        </div>
      )}

      <div className={styles.composer}>
        <input
          className={styles.input}
          placeholder={t("Send a message to start a conversation...")}
          value={input}
          onChange={(event) => setInput(event.target.value)}
          onKeyDown={handleKeyDown}
          disabled={pending}
        />
        <button
          type="button"
          className={styles.sendBtn}
          aria-label="Send"
          onClick={() => void handleSend()}
          disabled={pending || !input.trim()}
          style={
            input.trim() && !pending ? { backgroundColor: accent } : undefined
          }
        >
          <SendIcon />
        </button>
      </div>
    </div>
  );
};

export default Sandbox;
